import { useState, useEffect, useCallback, useMemo } from "react";
import api, { downloadBlob } from "@/lib/api";
import { toast } from "sonner";
import { Boxes, Loader2, AlertCircle, ChevronDown, ChevronRight, Download } from "lucide-react";

// Fix WH49 (2026-09-17): la pantalla que pidió Cristian por video, para dejar de
// llenar a mano su Excel "DASHBOARD GENERAL CHIZY CHIZ". Son sus dos hojas que
// más tiempo le comen: DASHBOARD DE VENTAS (platillos por categoria, con margen)
// y CONSUMO MENSUAL (insumos que eso consumio, una columna por sucursal).
//
// Todo sale de multiplicar las unidades vendidas por la receta. Verificado: la
// colección de ventas trae los MISMOS numeros que el escribio a mano — EXTRA
// PEPPERONI 4368 en Xalisco y 2690 en Hermosillo en agosto.

const hoyISO = () => new Date().toISOString().slice(0, 10);
const primeroDeMes = () => { const d = new Date(); d.setDate(1); return d.toISOString().slice(0, 10); };
const fmtMXN = n => n != null
  ? `$${Number(n).toLocaleString("es-MX", { maximumFractionDigits: 0 })}` : "—";
const fmtNum = n => n == null ? "—"
  : Number(n).toLocaleString("es-MX", { maximumFractionDigits: 2 });

// Fix WH67 (2026-10-01): Cristian (video del 01/10) pidió ordenar la lista
// (alfabético, más vendidas, mayor margen) y cuatro tarjetas debajo de las de
// arriba: más vendido, menos vendido, mayor margen y menor margen.
// Decisiones de Ramiro: el ranking se calcula sobre la categoría elegida en el
// desplegable; "margen" = pesos ganados (venta - costo); el orden aplica a las
// categorías y a los platillos de adentro. backend/consumo_excel.py tiene
// estas MISMAS reglas para que el Excel salga igual: si se cambia una, se
// cambia en los dos lados.
const TODAS = "__todas";
const ORDENES = [["vendidas", "Más vendidas"], ["az", "A–Z"], ["margen", "Mayor margen"]];
const claveNombre = s => String(s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
const cmpNombre = (a, b) => { const x = claveNombre(a), y = claveNombre(b); return x < y ? -1 : x > y ? 1 : 0; };
// Un platillo sin receta no tiene costo, así que no tiene margen: null.
const margenPesos = p => p.sin_receta ? null : Number(p.venta || 0) - Number(p.costo || 0);
const margenCategoria = c => {
  const con = (c.platillos || []).filter(p => !p.sin_receta);
  if (!con.length) return null;
  let total = 0;
  for (const p of con) total += Number(p.venta || 0) - Number(p.costo || 0);
  return total;
};
function ordenarLista(lista, orden, nombre, unidades, margen) {
  if (orden === "az") return [...lista].sort((a, b) => cmpNombre(nombre(a), nombre(b)));
  if (orden === "margen") {
    // Los que no tienen margen van al final, por nombre.
    const con = lista.filter(x => margen(x) != null).sort((a, b) => (margen(b) - margen(a)) || cmpNombre(nombre(a), nombre(b)));
    const sin = lista.filter(x => margen(x) == null).sort((a, b) => cmpNombre(nombre(a), nombre(b)));
    return [...con, ...sin];
  }
  return [...lista].sort((a, b) => (Number(unidades(b) || 0) - Number(unidades(a) || 0)) || cmpNombre(nombre(a), nombre(b)));
}
const ordenarCategorias = (categorias, orden) =>
  ordenarLista(categorias, orden, c => c.categoria, c => c.unidades, margenCategoria)
    .map(c => ({ ...c, platillos: ordenarLista(c.platillos || [], orden, p => p.platillo, p => p.unidades, margenPesos) }));
function rankingDe(categorias, categoria) {
  const platillos = categorias
    .filter(c => categoria === TODAS || c.categoria === categoria)
    .flatMap(c => (c.platillos || []).map(p => ({ ...p, categoria: c.categoria })));
  const vendidos = platillos.filter(p => Number(p.unidades || 0) > 0);
  const conMargen = platillos.filter(p => !p.sin_receta && Number(p.venta || 0) > 0);
  const primero = (lista, cmp) => (lista.length ? [...lista].sort(cmp)[0] : null);
  return {
    mas_vendido: primero(vendidos, (a, b) => (b.unidades - a.unidades) || cmpNombre(a.platillo, b.platillo)),
    menos_vendido: primero(vendidos, (a, b) => (a.unidades - b.unidades) || cmpNombre(a.platillo, b.platillo)),
    mayor_margen: primero(conMargen, (a, b) => (margenPesos(b) - margenPesos(a)) || cmpNombre(a.platillo, b.platillo)),
    menor_margen: primero(conMargen, (a, b) => (margenPesos(a) - margenPesos(b)) || cmpNombre(a.platillo, b.platillo)),
  };
}

export default function InventarioConsumo() {
  const [desde, setDesde] = useState(primeroDeMes);
  const [hasta, setHasta] = useState(hoyISO);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [abiertas, setAbiertas] = useState({});
  const [tab, setTab] = useState("ventas");
  // Fix WH67 (2026-10-01): orden de la lista, categoría del ranking y descarga.
  const [orden, setOrden] = useState("vendidas");
  const [catRanking, setCatRanking] = useState(TODAS);
  const [descargando, setDescargando] = useState(false);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/ops/inventario/consumo?desde=${desde}&hasta=${hasta}`);
      setData(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo cargar el consumo");
      setData(null);
    } finally { setLoading(false); }
  }, [desde, hasta]);

  useEffect(() => { cargar(); }, [cargar]);

  const sucs = (data?.sucursales || []).map(s => s.branch_name);
  const toggle = k => setAbiertas(a => ({ ...a, [k]: !a[k] }));

  // Fix WH67 (2026-10-01): categorías y platillos en el orden elegido, y el
  // ranking sobre la categoría elegida. Si esa categoría no aparece en el
  // período nuevo, se vuelve a "Todas".
  const categoriasOrdenadas = useMemo(() => ordenarCategorias(data?.categorias || [], orden), [data, orden]);
  const categoriasAZ = useMemo(() => ordenarCategorias(data?.categorias || [], "az"), [data]);
  const catRankingVigente = (data?.categorias || []).some(c => c.categoria === catRanking) ? catRanking : TODAS;
  const rk = useMemo(() => rankingDe(data?.categorias || [], catRankingVigente), [data, catRankingVigente]);

  const descargarExcel = async () => {
    setDescargando(true);
    try {
      const qs = new URLSearchParams({ desde, hasta, orden, ranking: catRankingVigente });
      const r = await api.get(`/ops/inventario/consumo/export.xlsx?${qs.toString()}`, { responseType: "blob" });
      downloadBlob(r.data, `consumo_${desde}_${hasta}.xlsx`);
    } catch (err) {
      toast.error(err.response?.status === 404 ? "No hay datos para ese período" : "No se pudo descargar el Excel");
    } finally { setDescargando(false); }
  };

  return (
    <div className="p-6 space-y-5" data-testid="inventario-consumo-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Boxes className="w-6 h-6 text-slate-400" /> Consumo de insumos
          </h1>
          <p className="text-sm text-slate-500">
            Lo que se vendió en el período y los insumos que eso consumió, según la receta de cada sucursal.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <input type="date" value={desde} onChange={e => setDesde(e.target.value)}
            className="border border-slate-200 rounded-lg px-3 py-1.5 text-sm" data-testid="consumo-desde" />
          <input type="date" value={hasta} onChange={e => setHasta(e.target.value)}
            className="border border-slate-200 rounded-lg px-3 py-1.5 text-sm" data-testid="consumo-hasta" />
          {/* Fix WH67 (2026-10-01): baja a Excel lo que se ve, en el orden elegido. */}
          {data?.disponible && (
            <button onClick={descargarExcel} disabled={descargando}
              className="flex items-center gap-1.5 border border-slate-200 rounded-lg px-3 py-1.5 text-sm text-slate-700 bg-white hover:bg-slate-50 disabled:opacity-50"
              data-testid="consumo-excel">
              {descargando ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              Excel
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="p-12 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
      ) : !data || !data.disponible ? (
        <div className="p-8 text-center text-sm text-slate-500 bg-white rounded-xl border border-slate-200"
             data-testid="consumo-sin-datos">
          {data?.motivo || "No hay datos para ese período."}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
            <Tarjeta t="Venta del período" v={fmtMXN(data.venta_total)} s={`${sucs.length} sucursales`} />
            <Tarjeta t="Costo de insumos" v={fmtMXN(data.costo_total)} s="según receta" />
            <Tarjeta t="Margen" v={fmtMXN(data.venta_total - data.costo_total)}
              s={data.venta_total > 0
                ? `${Math.round((data.venta_total - data.costo_total) / data.venta_total * 100)}%` : ""} />
            <Tarjeta t="Cobertura" v={`${data.cobertura_pct}%`}
              s={`${fmtNum(data.unidades_sin_receta)} unidades sin receta`} />
          </div>

          {/* Fix WH67 (2026-10-01): más/menos vendido y mayor/menor margen (en
              pesos) de la categoría elegida. */}
          <div className="space-y-2" data-testid="consumo-ranking">
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <span>Ranking de</span>
              <select value={catRankingVigente} onChange={e => setCatRanking(e.target.value)}
                className="border border-slate-200 rounded-lg px-2 py-1 text-xs text-slate-700 bg-white"
                data-testid="consumo-ranking-categoria">
                <option value={TODAS}>Todas las categorías</option>
                {categoriasAZ.map(c => <option key={c.categoria} value={c.categoria}>{c.categoria}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <TarjetaRanking t="Más vendido" p={rk.mas_vendido} dato="unidades"
                todas={catRankingVigente === TODAS} testid="consumo-rk-mas-vendido" />
              <TarjetaRanking t="Menos vendido" p={rk.menos_vendido} dato="unidades"
                todas={catRankingVigente === TODAS} testid="consumo-rk-menos-vendido" />
              <TarjetaRanking t="Mayor margen" p={rk.mayor_margen} dato="margen"
                todas={catRankingVigente === TODAS} testid="consumo-rk-mayor-margen" />
              <TarjetaRanking t="Menor margen" p={rk.menor_margen} dato="margen"
                todas={catRankingVigente === TODAS} testid="consumo-rk-menor-margen" />
            </div>
          </div>

          {data.cobertura_pct < 100 && (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-800"
                 data-testid="consumo-cobertura">
              <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              <span>
                El desglose cubre el <b>{data.cobertura_pct}%</b> de lo vendido.
                {" "}<b>{(data.platillos_sin_receta || []).length}</b> platillos
                {" "}({fmtNum(data.unidades_sin_receta)} unidades) no tienen receta cargada en Wansoft
                y no están contados en el consumo. Se listan abajo.
              </span>
            </div>
          )}

          <div className="flex items-end justify-between gap-2 flex-wrap border-b border-slate-200">
            <div className="flex gap-1">
              {[["ventas", "Ventas por categoría"], ["insumos", "Consumo de insumos"],
                ["sin", `Sin receta (${(data.platillos_sin_receta || []).length})`]].map(([k, l]) => (
                <button key={k} onClick={() => setTab(k)}
                  className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                    tab === k ? "border-[#FFDF00] text-slate-900" : "border-transparent text-slate-500 hover:text-slate-700"}`}
                  data-testid={`consumo-tab-${k}`}>{l}</button>
              ))}
            </div>
            {/* Fix WH67 (2026-10-01): ordenar categorías y platillos. */}
            {tab === "ventas" && (
              <div className="flex items-center gap-1 pb-1.5 text-xs" data-testid="consumo-orden">
                <span className="text-slate-400 mr-1">Ordenar:</span>
                {ORDENES.map(([k, l]) => (
                  <button key={k} onClick={() => setOrden(k)}
                    className={`px-2.5 py-1 rounded-md border transition-colors ${
                      orden === k ? "bg-[#FFDF00] border-[#FFDF00] text-slate-900 font-medium"
                                  : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"}`}
                    data-testid={`consumo-orden-${k}`}>{l}</button>
                ))}
              </div>
            )}
          </div>

          {tab === "ventas" && (
            <div className="space-y-2" data-testid="consumo-ventas">
              {categoriasOrdenadas.map(c => (
                <div key={c.categoria} className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                  <button onClick={() => toggle(c.categoria)}
                    className="w-full flex items-center justify-between gap-3 p-3 hover:bg-slate-50 transition-colors">
                    <div className="flex items-center gap-2 min-w-0">
                      {abiertas[c.categoria] ? <ChevronDown className="w-4 h-4 text-slate-400" />
                                             : <ChevronRight className="w-4 h-4 text-slate-400" />}
                      <span className="text-sm font-semibold text-slate-800 truncate">{c.categoria}</span>
                      <span className="text-xs text-slate-400">{c.platillos.length} platillos</span>
                    </div>
                    <div className="flex items-center gap-4 shrink-0 text-sm">
                      <span className="text-slate-500">{fmtNum(c.unidades)} u</span>
                      <span className="font-semibold text-slate-900">{fmtMXN(c.venta)}</span>
                      {/* Fix WH50 (2026-09-17): una categoria cuyos platillos no
                          tienen receta no tiene con que calcular margen, y el
                          backend devuelve null. Sin esto se leia "null%". */}
                      <span className="text-emerald-700 font-medium w-14 text-right">
                        {c.margen_pct == null ? "—" : `${c.margen_pct}%`}</span>
                    </div>
                  </button>
                  {abiertas[c.categoria] && (
                    <div className="overflow-x-auto border-t border-slate-100">
                      <table className="w-full text-xs">
                        <thead className="bg-slate-50 text-slate-500">
                          <tr>
                            <th className="text-left p-2 font-medium">Platillo</th>
                            {sucs.map(s => <th key={s} className="text-right p-2 font-medium">{s}</th>)}
                            <th className="text-right p-2 font-medium">Total u</th>
                            <th className="text-right p-2 font-medium">Venta</th>
                            <th className="text-right p-2 font-medium">Costo</th>
                            <th className="text-right p-2 font-medium">Margen</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-50">
                          {c.platillos.map(p => (
                            <tr key={p.platillo} className="hover:bg-slate-50">
                              <td className="p-2 text-slate-800">
                                {p.platillo}
                                {p.sin_receta && <span className="ml-1 text-amber-600" title="sin receta">·</span>}
                              </td>
                              {sucs.map(s => <td key={s} className="p-2 text-right font-mono text-slate-600">
                                {p.por_sucursal[s] ? fmtNum(p.por_sucursal[s]) : "·"}</td>)}
                              <td className="p-2 text-right font-mono font-medium">{fmtNum(p.unidades)}</td>
                              <td className="p-2 text-right font-mono">{fmtMXN(p.venta)}</td>
                              <td className="p-2 text-right font-mono text-slate-500">
                                {p.sin_receta ? "—" : fmtMXN(p.costo)}</td>
                              <td className="p-2 text-right font-mono text-emerald-700">
                                {p.sin_receta ? "—" : `${p.margen_pct}%`}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {tab === "insumos" && (
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="consumo-insumos">
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 text-slate-500">
                    <tr>
                      <th className="text-left p-2 font-medium">SKU</th>
                      <th className="text-left p-2 font-medium">Insumo</th>
                      <th className="text-left p-2 font-medium">Un.</th>
                      {sucs.map(s => <th key={s} className="text-right p-2 font-medium">{s}</th>)}
                      <th className="text-right p-2 font-medium">Total</th>
                      <th className="text-right p-2 font-medium">Costo un.</th>
                      <th className="text-right p-2 font-medium">Costo total</th>
                      <th className="text-right p-2 font-medium">Stock hoy</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50">
                    {data.insumos.map(i => (
                      <tr key={i.insumo} className="hover:bg-slate-50">
                        <td className="p-2 font-mono text-slate-400">{i.codigo || "—"}</td>
                        <td className="p-2 text-slate-800">
                          {i.insumo}
                          <span className="text-slate-300 ml-1">{i.departamento}</span>
                        </td>
                        <td className="p-2 text-slate-500">{i.unidad}</td>
                        {sucs.map(s => <td key={s} className="p-2 text-right font-mono text-slate-600">
                          {i.por_sucursal[s] ? fmtNum(i.por_sucursal[s]) : "·"}</td>)}
                        <td className="p-2 text-right font-mono font-medium">{fmtNum(i.total)}</td>
                        <td className="p-2 text-right font-mono text-slate-500">
                          {i.costo_unitario > 0 ? fmtMXN(i.costo_unitario) : <span className="text-amber-600">sin precio</span>}
                          {i.costo_calculado && <span className="text-slate-300" title="calculado desde la receta"> ·</span>}
                        </td>
                        <td className="p-2 text-right font-mono">{fmtMXN(i.costo_total)}</td>
                        <td className="p-2 text-right font-mono text-slate-400">{fmtNum(i.stock_total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="p-3 text-[11px] text-slate-400 border-t border-slate-100">{data.stock_nota}</div>
            </div>
          )}

          {tab === "sin" && (
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="consumo-sin-receta">
              {(data.platillos_sin_receta || []).length === 0 ? (
                <div className="p-8 text-center text-sm text-slate-400">
                  Todos los platillos vendidos tienen su receta cargada.
                </div>
              ) : (
                <>
                  <div className="p-3 text-xs text-slate-500 border-b border-slate-100">
                    Estos platillos se vendieron pero no tienen receta en Wansoft, así que no se
                    puede saber qué insumos consumieron. Cargándolos allá, entran solos.
                  </div>
                  <table className="w-full text-xs">
                    <thead className="bg-slate-50 text-slate-500">
                      <tr>
                        <th className="text-left p-2 font-medium">Platillo</th>
                        <th className="text-left p-2 font-medium">Categoría</th>
                        <th className="text-left p-2 font-medium">Sucursales</th>
                        <th className="text-right p-2 font-medium">Unidades</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-50">
                      {data.platillos_sin_receta.map(p => (
                        <tr key={p.platillo} className="hover:bg-slate-50">
                          <td className="p-2 text-slate-800">{p.platillo}</td>
                          <td className="p-2 text-slate-500">{p.categoria}</td>
                          <td className="p-2 text-slate-500">{(p.sucursales || []).join(", ")}</td>
                          <td className="p-2 text-right font-mono">{fmtNum(p.unidades)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// Fix WH67 (2026-10-01): tarjeta de un platillo del ranking. `dato` decide qué
// número va grande: las unidades ("cuántas se desplazaron") o el margen en pesos.
function TarjetaRanking({ t, p, dato, todas, testid }) {
  const m = p ? margenPesos(p) : null;
  const pct = p && m != null && Number(p.venta) > 0 ? Math.round(m / Number(p.venta) * 100) : null;
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4 min-w-0" data-testid={testid}>
      <div className="text-xs text-slate-400">{t}</div>
      {p ? (
        <>
          <div className="text-sm font-semibold text-slate-800 truncate" title={p.platillo}>{p.platillo}</div>
          <div className="text-xl font-bold text-slate-900">
            {dato === "margen" ? fmtMXN(m) : `${fmtNum(p.unidades)} u`}
          </div>
          <div className="text-xs text-slate-400 truncate">
            {dato === "margen"
              ? `${fmtNum(p.unidades)} u${pct != null ? ` · ${pct}% de margen` : ""}`
              : (m == null ? "sin receta" : `${fmtMXN(m)} de margen`)}
            {todas ? ` · ${p.categoria}` : ""}
          </div>
        </>
      ) : (
        <div className="text-sm text-slate-400 mt-1">Sin datos</div>
      )}
    </div>
  );
}

function Tarjeta({ t, v, s }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4">
      <div className="text-xs text-slate-400">{t}</div>
      <div className="text-2xl font-bold text-slate-900">{v}</div>
      <div className="text-xs text-slate-400">{s}</div>
    </div>
  );
}
